import { useTranslation } from "react-i18next";
import { tx, loc } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { InstallPWA } from "@/components/InstallPWA";
import { LanguageSelector } from "@/components/LanguageSelector";
import { toast } from "sonner";
import { ChevronRight, LogOut, Download, Trash2, Crown, Bell, ShieldCheck, Waves, BookX, Mail, FileText, CreditCard, Flame, UserRound } from "lucide-react";

const AREAS = [["emozioni",tx("Emozioni")],["relazioni",tx("Relazioni")],["autostima",tx("Autostima")],["cambiamento",tx("Cambiamento")],["direzione",tx("Direzione di vita")],["creativita",tx("Creativita")],["spiritualita",tx("Spiritualita")],["calma",tx("Calma interiore")]];
const TONES = [["delicato",tx("Delicato e rassicurante")],["diretto",tx("Diretto e concreto")],["simbolico",tx("Simbolico e spirituale")],["equilibrato",tx("Equilibrato")]];

export default function Profilo() {
  useTranslation();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: "", area: "emozioni", tone: "equilibrato", reminder_time: "20:00", notifications_enabled: true });
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (user) setForm({
      display_name: user.display_name || "", area: user.area || "emozioni",
      tone: user.tone || "equilibrato", reminder_time: user.reminder_time || "20:00",
      notifications_enabled: user.notifications_enabled !== false,
    });
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [user]);

  const save = async (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    try { const { data } = await api.put("/profile", patch); setUser(data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "i-miei-dati-anima.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success(tx("Dati esportati"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteDiary = async () => {
    if (!window.confirm(tx("Eliminare tutte le pagine del tuo diario? Le riflessioni verranno rimosse in modo permanente, ma il tuo account restera attivo. L'azione non e reversibile."))) return;
    try { await api.delete("/account/diary"); toast.success(tx("Diario eliminato")); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Stai per eliminare DEFINITIVAMENTE il tuo account.\n\nConseguenze: verranno cancellati per sempre il tuo profilo, tutte le riflessioni del diario, i simboli ricevuti e le tue preferenze. Perderai l'accesso e l'azione non e reversibile.\n\nVuoi procedere?")) return;
    if (!window.confirm(tx("Confermi definitivamente l'eliminazione dell'account? Questa azione non puo essere annullata."))) return;
    try { await api.delete("/account"); toast.success(tx("Account eliminato")); setUser(false); navigate("/"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openPortal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const resendVerification = async () => {
    try { await api.post("/auth/resend-verification"); toast.success(tx("Email di verifica inviata")); }
    catch { toast.error(tx("Riprova piu tardi")); }
  };

  const cancelSub = async () => {
    if (!window.confirm(tx("Vuoi disattivare il rinnovo automatico? Manterrai l'accesso fino alla fine del periodo gia pagato."))) return;
    try { await api.post("/subscription/cancel"); toast.success(tx("Rinnovo automatico disattivato")); api.get("/subscription").then((r) => setSub(r.data)); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(loc(), { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; } };
  const fmtEur = (v) => (v == null ? "" : v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20ac");
  const intLabel = (i) => (i === "year" ? "all'anno" : "al mese");

  const statusLabel = {
    active: tx("Abbonamento attivo"), trialing: tx("Prova gratuita in corso"), canceled: tx("Rinnovo disattivato"), expired: tx("Abbonamento scaduto"), null: tx("Nessun abbonamento"),
  };

  if (!user) return null;

  return (
    <AppShell>
      <div className="px-7 pt-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-anima-sage/30 flex items-center justify-center overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-3xl text-anima-sagedeep">{(user.display_name || "A")[0]}</span>}
          </div>
          <div>
            <h1 className="font-serif text-3xl text-anima-ink leading-none">{user.display_name}</h1>
            <p className="text-sm text-anima-muted">{user.email}</p>
          </div>
        </div>

        {/* Subscription */}
        <div className="rounded-3xl bg-anima-gold/15 border border-anima-gold/30 p-5 mb-6" data-testid="profile-subscription">
          <div className="flex items-center gap-2 mb-1">
            <Crown size={17} className="text-anima-golddeep" />
            <p className="text-sm text-anima-ink font-medium" data-testid="sub-status-label">
              {sub?.lifetime_access ? tx("Accesso autore permanente") : (statusLabel[sub?.status] || statusLabel.null)}
            </p>
          </div>
          {!sub?.lifetime_access && (
            <>
              {sub?.status === "trialing" && sub?.trial_ends_at && (
                <p className="text-xs text-anima-muted" data-testid="sub-trial-end">{tx("La prova gratuita termina il")} {fmtDate(sub.trial_ends_at)}.</p>
              )}
              {sub?.status === "active" && (
                <div className="text-xs text-anima-muted space-y-0.5">
                  {sub?.plan_name && <p>{tx("Piano")} {sub.plan_name}.</p>}
                  {sub?.cancel_at_period_end ? (
                    <p data-testid="sub-renewal-info">{tx("Rinnovo automatico disattivato. Accesso attivo")}{sub?.current_period_end ? ` fino al ${fmtDate(sub.current_period_end)}` : tx(" fino alla fine del periodo pagato")}.</p>
                  ) : (
                    sub?.current_period_end && (
                      <p data-testid="sub-renewal-info">{tx("Prossimo rinnovo:")} {fmtDate(sub.current_period_end)} &middot; {fmtEur(sub.renewal_amount)} {intLabel(sub.renewal_interval)}.</p>
                    )
                  )}
                </div>
              )}
            </>
          )}
          {!sub?.lifetime_access && (
            <div className="flex gap-2 mt-3 flex-wrap">
              <Link to="/pricing" data-testid="profile-plans-btn" className="rounded-full bg-anima-sagedeep text-anima-ivory px-5 py-2.5 text-sm">{tx("Vedi i piani")}</Link>
              {sub?.status === "active" && (
                <>
                  <button onClick={openPortal} data-testid="profile-portal-btn" className="rounded-full border border-anima-ink/15 text-anima-ink px-5 py-2.5 text-sm flex items-center gap-1.5"><CreditCard size={14} />{tx("Gestisci pagamento")}</button>
                  {!sub?.cancel_at_period_end && (
                    <button onClick={cancelSub} data-testid="profile-cancel-sub" className="rounded-full border border-anima-ink/15 text-anima-muted px-5 py-2.5 text-sm">{tx("Disattiva il rinnovo automatico")}</button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {user.email_verified === false && (
          <button onClick={resendVerification} data-testid="profile-verify-email" className="w-full mb-6 flex items-center gap-3 rounded-2xl bg-anima-pink/50 border border-anima-ink/5 px-5 py-3.5 text-left">
            <Mail size={17} className="text-anima-golddeep" />
            <span className="flex-1 text-sm text-anima-ink">{tx("Verifica la tua email")}<span className="text-anima-muted">{tx("— invia di nuovo")}</span></span>
            <ChevronRight size={16} className="text-anima-muted" />
          </button>
        )}

        {/* Install PWA */}
        <div className="mb-6">
          <div className="flex items-center justify-between rounded-3xl bg-white/55 border border-anima-ink/5 px-5 py-4 mb-3" data-testid="profile-language-row">
            <span className="text-base text-anima-ink">{tx("Lingua / Language")}</span>
            <LanguageSelector />
          </div>
          <InstallPWA />
        </div>

        {/* Preferences */}
        <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3 ml-1">{tx("Preferenze")}</p>
        <div className="space-y-4 mb-8">
          <Field label={tx("Nome")}>
            <input data-testid="profile-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} onBlur={(e) => save({ display_name: e.target.value })}
              className="bg-transparent text-right outline-none text-anima-ink w-40" />
          </Field>
          <Field label={tx("Area")} icon={Waves}>
            <select data-testid="profile-area" value={form.area} onChange={(e) => save({ area: e.target.value })} className="bg-transparent text-right outline-none text-anima-ink">
              {AREAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label={tx("Tono")}>
            <select data-testid="profile-tone" value={form.tone} onChange={(e) => save({ tone: e.target.value })} className="bg-transparent text-right outline-none text-anima-ink max-w-[190px]">
              {TONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label={tx("Promemoria")} icon={Bell}>
            <input data-testid="profile-time" type="time" value={form.reminder_time} onChange={(e) => save({ reminder_time: e.target.value })} className="bg-transparent text-right outline-none text-anima-ink" />
          </Field>
          <Field label={tx("Notifiche")}>
            <button data-testid="profile-notifications" onClick={() => save({ notifications_enabled: !form.notifications_enabled })}
              className={`w-11 h-6 rounded-full transition-colors duration-300 relative ${form.notifications_enabled ? "bg-anima-sagedeep" : "bg-anima-ink/15"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-300 ${form.notifications_enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </Field>
        </div>

        {user.role === "admin" && (
          <Link to="/admin" data-testid="profile-admin-btn" className="flex items-center justify-between rounded-2xl bg-anima-purple/40 border border-anima-ink/5 px-5 py-4 mb-4">
            <span className="flex items-center gap-2 text-anima-ink"><ShieldCheck size={18} />{tx("Pannello amministratore")}</span>
            <ChevronRight size={18} className="text-anima-muted" />
          </Link>
        )}

        <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3 ml-1">{tx("Informazioni")}</p>
        <div className="space-y-3 mb-6">
          <Row onClick={() => navigate("/chi-sono")} icon={UserRound} label={tx("Chi ha ideato Ascolta Anima")} testid="profile-chisono" />
          <Row onClick={() => navigate("/ianas")} icon={Flame} label={tx("L'opera Ianas di Anna Checchi")} testid="profile-ianas" />
        </div>

        <p className="text-[11px] uppercase tracking-widest text-anima-muted mb-3 ml-1">{tx("Privacy e dati")}</p>
        <div className="space-y-3 mb-4">
          <Row onClick={exportData} icon={Download} label={tx("Esporta i miei dati")} testid="profile-export" />
          <Row onClick={deleteDiary} icon={BookX} label={tx("Elimina il diario")} testid="profile-delete-diary" />
          <Row onClick={deleteAccount} icon={Trash2} label={tx("Elimina account")} danger testid="profile-delete" />
        </div>
        <div className="rounded-2xl bg-white/40 border border-anima-ink/5 px-5 py-4 mb-4">
          <p className="text-xs text-anima-muted leading-relaxed">{tx("L'eliminazione del diario o dell'account e permanente e non reversibile. Prima di ogni azione irreversibile ti chiediamo una conferma esplicita.")}</p>
          <a href={`mailto:annaclaudiachecchi@gmail.com`} data-testid="profile-privacy-contact" className="mt-3 inline-flex items-center gap-2 text-sm text-anima-sagedeep">
            <Mail size={14} />{tx("Contatta il referente privacy")}</a>
          <div className="flex gap-4 mt-3">
            <Link to="/terms" className="inline-flex items-center gap-1.5 text-xs text-anima-muted underline"><FileText size={12} />{tx("Termini")}</Link>
            <Link to="/privacy" className="inline-flex items-center gap-1.5 text-xs text-anima-muted underline"><FileText size={12} />{tx("Privacy Policy")}</Link>
          </div>
        </div>
        <div className="space-y-3 mb-8">
          <Row onClick={doLogout} icon={LogOut} label={tx("Esci")} testid="profile-logout" />
        </div>

        <p className="text-[11px] text-anima-muted text-center leading-relaxed pb-4">{tx("Ascolta Anima e uno strumento di autoriflessione e non sostituisce professionisti sanitari o psicologici.")}</p>
      </div>
    </AppShell>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white/55 border border-anima-ink/5 px-5 py-3.5">
      <span className="flex items-center gap-2 text-anima-ink text-sm">{Icon && <Icon size={16} className="text-anima-muted" />}{label}</span>
      {children}
    </div>
  );
}
function Row({ onClick, icon: Icon, label, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center justify-between rounded-2xl bg-white/55 border border-anima-ink/5 px-5 py-4 ${danger ? "text-red-400/80" : "text-anima-ink"}`}>
      <span className="flex items-center gap-3 text-sm"><Icon size={17} /> {label}</span>
      <ChevronRight size={17} className="opacity-40" />
    </button>
  );
}
